import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { Group, Stack, Title } from "@mantine/core";
import { useCallbackRef, useDebouncedValue } from "@mantine/hooks";

import { usePracticeLedgerCsvExport } from "@/billing/hooks/usePracticeLedgerCsvExport";
import { usePracticeLedgerTableData } from "@/billing/hooks/usePracticeLedgerTableData";
import {
  DEFAULT_PRACTICE_LEDGER_DATE_RANGE_SELECTION,
  DEFAULT_PRACTICE_LEDGER_SORT,
  PRACTICE_LEDGER_PAGE_SIZE,
  PRACTICE_LEDGER_SEARCH_OPTION,
  type PracticeLedgerDateRangeSelection,
  type PracticeLedgerSearchOption,
  type PracticeLedgerSortColumn,
  type PracticeLedgerSortState,
} from "@/billing/types/practice-ledger";
import { resolvePracticeLedgerSelectionDateRange } from "@/billing/utils/practice-ledger-date-range-selection";
import { getNextTableSortState } from "@/billing/utils/table-sort";
import useCurrentDay from "@/utils/hooks/use-current-day";
import usePagination from "@/utils/hooks/use-pagination";

import { PRACTICE_LEDGER_SEARCH_DEBOUNCE_MS } from "./constants/practice-ledger";
import LedgerCsvExportButton from "./LedgerCsvExportButton";
import LedgerDateRangeControl from "./LedgerDateRangeControl";
import LedgerSearch from "./LedgerSearch";
import LedgerSummaryCards from "./LedgerSummaryCards";
import LedgerTable from "./LedgerTable";

interface LedgerProps {
  refreshKey?: number;
  onLoadingChange?: (loading: boolean) => void;
}

export default function Ledger({ refreshKey = 0, onLoadingChange }: LedgerProps) {
  const { practiceId } = useParams<{ practiceId: string }>();
  const [dateRangeSelection, setDateRangeSelection] = useState<PracticeLedgerDateRangeSelection>(
    DEFAULT_PRACTICE_LEDGER_DATE_RANGE_SELECTION,
  );
  // A named preset follows the calendar day, so a Ledger left open past midnight moves its table,
  // totals and export to the new day's window.
  const today = useCurrentDay();
  const dateRange = useMemo(
    () => resolvePracticeLedgerSelectionDateRange(dateRangeSelection, today),
    [dateRangeSelection, today],
  );
  const [query, setQuery] = useState("");
  const [searchOption, setSearchOption] = useState<PracticeLedgerSearchOption>(
    PRACTICE_LEDGER_SEARCH_OPTION.Client,
  );
  const [debouncedQuery] = useDebouncedValue(query, PRACTICE_LEDGER_SEARCH_DEBOUNCE_MS);
  const [sort, setSort] = useState<PracticeLedgerSortState>(DEFAULT_PRACTICE_LEDGER_SORT);
  const { currentPage, changeCurrentPage, changeTotalPages } = usePagination({
    pageSize: PRACTICE_LEDGER_PAGE_SIZE,
  });
  const changeCurrentPageCallback = useCallbackRef(changeCurrentPage);
  const onLoadingChangeCallback = useCallbackRef(onLoadingChange);
  const search = debouncedQuery.trim()
    ? { query: debouncedQuery, option: searchOption }
    : undefined;

  const { rows, totals, totalRows, isLoading, error } = usePracticeLedgerTableData(
    practiceId,
    dateRange,
    refreshKey,
    search,
    currentPage,
    sort,
  );
  const { isExporting, handleExportCsv } = usePracticeLedgerCsvExport({
    practiceId,
    dateRange,
    search,
    sort,
  });

  const freshTotalPages = totalRows > 0 ? Math.ceil(totalRows / PRACTICE_LEDGER_PAGE_SIZE) : 0;
  const isPageStale = freshTotalPages > 0 && currentPage > freshTotalPages;
  const isTableLoading = isLoading || isPageStale;

  useEffect(() => {
    changeTotalPages(totalRows);
  }, [changeTotalPages, totalRows]);

  useEffect(() => {
    if (isPageStale) changeCurrentPage(freshTotalPages);
  }, [changeCurrentPage, freshTotalPages, isPageStale]);

  // When midnight moves a named preset's window, the rows shift by a day, so the Ledger goes back
  // to page 1 the same way a user-chosen window change does. Current Month keeps its bounds until
  // the month ends, so the page stays put on the other midnights.
  const previousDateRangeRef = useRef(dateRange);
  useEffect(() => {
    const previousDateRange = previousDateRangeRef.current;
    previousDateRangeRef.current = dateRange;
    if (
      previousDateRange.startDate.getTime() === dateRange.startDate.getTime() &&
      previousDateRange.endDate.getTime() === dateRange.endDate.getTime()
    )
      return;
    changeCurrentPageCallback(1);
  }, [changeCurrentPageCallback, dateRange]);

  useEffect(() => {
    onLoadingChangeCallback?.(isTableLoading);
  }, [isTableLoading, onLoadingChangeCallback]);

  useEffect(() => {
    return () => onLoadingChangeCallback?.(false);
  }, [onLoadingChangeCallback]);

  const handleDateRangeSelectionChange = (nextSelection: PracticeLedgerDateRangeSelection) => {
    setDateRangeSelection(nextSelection);
    changeCurrentPageCallback(1);
  };

  const handleSearchQueryChange = (nextQuery: string) => {
    setQuery(nextQuery);
    changeCurrentPageCallback(1);
  };

  const handleSearchOptionChange = (nextOption: PracticeLedgerSearchOption) => {
    setSearchOption(nextOption);
    changeCurrentPageCallback(1);
  };

  const handleSort = (columnKey: PracticeLedgerSortColumn) => {
    setSort((current) => {
      const next = getNextTableSortState(
        { columnKey: current.column, direction: current.direction },
        columnKey,
      );
      return { column: next.columnKey, direction: next.direction };
    });
    changeCurrentPageCallback(1);
  };

  return (
    <Stack gap="xl" data-testid="practice-ledger" data-refresh-key={refreshKey}>
      <Stack gap="lg">
        <Group gap="md" wrap="wrap" align="center" justify="space-between">
          <Title order={3} size="h1">
            Ledger
          </Title>
          <LedgerDateRangeControl
            dateRange={dateRange}
            preset={dateRangeSelection.preset}
            onSelectionChange={handleDateRangeSelectionChange}
          />
        </Group>
        <LedgerSummaryCards totals={isTableLoading || error ? undefined : totals} />
      </Stack>
      <Stack gap="lg">
        <Group gap="md" wrap="wrap" align="center" justify="space-between">
          <Group gap="lg" wrap="wrap" align="center">
            <Title order={4} size="h2">
              Recent Activity
            </Title>
            <LedgerSearch
              query={query}
              searchOption={searchOption}
              onQueryChange={handleSearchQueryChange}
              onSearchOptionChange={handleSearchOptionChange}
            />
          </Group>
          <LedgerCsvExportButton isExporting={isExporting} onExport={handleExportCsv} />
        </Group>
        <LedgerTable
          rows={rows}
          error={error}
          onSort={handleSort}
          totalRows={totalRows}
          currentPage={currentPage}
          isLoading={isTableLoading}
          isSearching={!!query.trim()}
          onPageChange={changeCurrentPage}
          pageSize={PRACTICE_LEDGER_PAGE_SIZE}
          sortState={{ columnKey: sort.column, direction: sort.direction }}
        />
      </Stack>
    </Stack>
  );
}
